import { FC, useState } from 'react';
import classnames from 'classnames';
import Autocomplete from '@mui/material/Autocomplete';
import { TextField, Popper, CheckIcon, ExpandMore, ExpandLess, ClickAwayListener, Box } from '../uiLibraryImports';
import { AutocompleteProps } from './autocompleteTypes';
import styles from './autocomplete.module.scss';
import Tooltip from '../tooltip';

const AutocompleteWrapper: FC<AutocompleteProps> = (props: AutocompleteProps) => {
    const { list, selectedUuid, setSelectedUuid, title, padding = '0 2rem', disabled } = props;
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState('');

    const CustomPopper = popperProps => {
        return <Popper {...popperProps} className={classnames(popperProps.className, styles['select-course'])} />;
    };

    const renderOption = props => {
        return (
            <Box
                component="li"
                sx={{ width: '50rem' }}
                {...props}
                key={props.uuid}
                onClick={() => !props.disabled && setSelectedUuid(props.uuid)}
            >
                <div
                    className={
                        props.uuid === selectedUuid
                            ? styles['selected-option']
                            : classnames(styles.option, { [styles.disabled]: props.disabled })
                    }
                >
                    <div>{props.displayName}</div>
                    {props.uuid === selectedUuid && (
                        <CheckIcon data-testid="check-icon" fontSize="small" className={styles.icon} />
                    )}
                </div>
            </Box>
        );
    };

    return (
        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
            <div
                className={classnames(styles['select-course-wrapper'], {
                    [styles['select-course-wrapper-hovered']]: isHovered || isOpen
                })}
                style={{ '--padding': padding } as any}
                onMouseOver={(): void => setIsHovered(true)}
                onMouseLeave={(): void => setIsHovered(false)}
                onClick={() => !disabled && setIsOpen(prev => !prev)}
            >
                {title && <div className={styles.text}>{title}</div>}
                <Autocomplete
                    sx={{
                        width: '15rem',
                        opacity: disabled ? 0.5 : 1,
                        ...(disabled && { '& .MuiInputBase-root': { cursor: 'not-allowed' } })
                    }}
                    open={isOpen}
                    value={list.find(option => option.uuid === selectedUuid)}
                    inputValue={inputValue}
                    onInputChange={(_, value) => {
                        setInputValue(value);
                    }}
                    options={list}
                    getOptionLabel={option => option.displayName}
                    renderOption={(_, props) =>
                        props.tooltip ? (
                            <Tooltip
                                title={props.tooltip}
                                placement="top-start"
                                disablePortal={false}
                                popperStyle={{ zIndex: 2000 }}
                            >
                                {renderOption(props)}
                            </Tooltip>
                        ) : (
                            renderOption(props)
                        )
                    }
                    PopperComponent={CustomPopper}
                    disableClearable
                    onClose={() => setIsHovered(false)}
                    popupIcon={
                        isOpen ? (
                            <ExpandLess
                                className={styles.icon}
                                key="expandLess"
                                sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                            />
                        ) : (
                            <ExpandMore
                                className={styles.icon}
                                key="expandMore"
                                sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                            />
                        )
                    }
                    renderInput={params => {
                        return (
                            <TextField
                                sx={{
                                    backgroundColor: 'rgba(38, 35, 58, 0.5)',
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    border: '1px solid rgba(255, 255, 255, 0.25)',
                                    borderRadius: '4px',
                                    '& .MuiInputBase-input.Mui-disabled': {
                                        WebkitTextFillColor: 'rgba(255, 255, 255, 0.7)'
                                    },
                                    ...(disabled && { 'input, button': { cursor: 'not-allowed' } })
                                }}
                                className={disabled ? styles['disabled-text-field'] : ''}
                                {...params}
                            />
                        );
                    }}
                />
            </div>
        </ClickAwayListener>
    );
};

export default AutocompleteWrapper;
